import { Button } from "@design-lab/shared/ui";
import { Minus, Plus } from "lucide-react";
import { trackZoomClicked } from "@design-lab/shared/lib/analytics";
import { useEffect } from "react";
import { usePlacementZoomAndPan } from "@design-lab/features/canvas/hooks/usePlacementZoomAndPan";

type ZoomProps = {
  maxZoom?: number;
  initialZoom?: number;
};

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 1;
const DEFAULT_INITIAL_ZOOM = 2; // 200% for design lab

export function Zoom({ maxZoom = 10, initialZoom = DEFAULT_INITIAL_ZOOM }: ZoomProps) {
  const { zoom, setZoom, setPan } = usePlacementZoomAndPan(MIN_ZOOM, maxZoom);

  const zoomPercentage = Math.round(zoom * 100);

  const handleZoomIn = () => {
    const newZoom = Math.min(zoom + ZOOM_STEP, maxZoom);
    setZoom(newZoom);
    trackZoomClicked(newZoom);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(zoom - ZOOM_STEP, MIN_ZOOM);
    setZoom(newZoom);
    trackZoomClicked(newZoom);
  };

  // Initialize zoom on mount
  useEffect(() => {
    const clamped = Math.max(MIN_ZOOM, Math.min(initialZoom, maxZoom));
    setZoom(clamped);
    setPan({ x: 0, y: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="absolute left-2 top-2 z-10 flex h-9 items-center rounded-md bg-white shadow-md lg:left-auto lg:right-4">
      <Button
        variant="icon"
        size="icon"
        aria-label="Zoom Out"
        onPress={handleZoomOut}
        isDisabled={zoom <= MIN_ZOOM}
        data-testid="zoom-out"
        className="h-9 w-6 p-0"
      >
        <Minus className="size-4" />
      </Button>
      <span
        className="flex h-9 min-w-12 flex-col items-center justify-center border-l border-r text-center text-sm font-medium"
        data-testid="zoom-percentage"
      >
        {zoomPercentage}%
      </span>
      <Button
        variant="icon"
        size="icon"
        aria-label="Zoom In"
        onPress={handleZoomIn}
        isDisabled={zoom >= maxZoom}
        data-testid="zoom-in"
        className="h-9 w-6 p-0"
      >
        <Plus className="size-4" />
      </Button>
    </div>
  );
}
